import { useState, useEffect } from "react";
import api from "@/utils/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Award, CalendarDays, GraduationCap, AlertTriangle, CheckCircle, Clock, Play, Mail, Settings, Plus, X } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import ThreeWindowRow from "@/components/ThreeWindowRow";

const COLORS = ["#0284C7", "#D97706", "#DC2626"];

export default function DashboardPage() {
  useDocumentTitle("Dashboard", "Key metrics across members, certificates, courses and revenue.");
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [todayData, setTodayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningCheck, setRunningCheck] = useState(false);
  const [editingWindows, setEditingWindows] = useState(false);
  const [windows, setWindows] = useState([]);
  const [newWindow, setNewWindow] = useState("");
  const [editingAttWindows, setEditingAttWindows] = useState(false);
  const [attWindows, setAttWindows] = useState([]);
  const [newAttWindow, setNewAttWindow] = useState("");
  const [runningAtt, setRunningAtt] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/dashboard"),
      api.get("/scheduler/status"),
      api.get("/analytics/today"),
    ]).then(([s, sch, td]) => { setStats(s.data); setScheduler(sch.data); setWindows(sch.data.reminder_windows || []); setAttWindows(sch.data.attendance_windows || []); setTodayData(td.data); setLoading(false); })
    .catch((err) => { if (!err?.response?.status || err.response.status >= 500) console.error("Dashboard load error:", err); setLoading(false); });
  }, []);

  const runExpiryCheck = async () => {
    setRunningCheck(true);
    try {
      const { data } = await api.post("/scheduler/run-now");
      toast.success(`Check complete: ${data.result?.sent || 0} sent, ${data.result?.skipped || 0} skipped`);
      const sch = await api.get("/scheduler/status");
      setScheduler(sch.data);
    } catch { toast.error("Failed to run check"); }
    finally { setRunningCheck(false); }
  };

  const addWindow = () => {
    const d = parseInt(newWindow);
    if (d && d >= 1 && d <= 365 && !windows.includes(d)) {
      setWindows([...windows, d].sort((a, b) => b - a));
      setNewWindow("");
    }
  };

  const removeWindow = (d) => setWindows(windows.filter(w => w !== d));

  const saveWindows = async () => {
    try {
      await api.put("/scheduler/settings", { windows, enabled: true });
      toast.success("Reminder windows updated");
      setEditingWindows(false);
      const sch = await api.get("/scheduler/status");
      setScheduler(sch.data);
    } catch { toast.error("Failed to save settings"); }
  };

  const addAttWindow = () => {
    const d = parseInt(newAttWindow);
    if (d && d >= 1 && d <= 60 && !attWindows.includes(d)) {
      setAttWindows([...attWindows, d].sort((a, b) => b - a));
      setNewAttWindow("");
    }
  };

  const removeAttWindow = (d) => setAttWindows(attWindows.filter(w => w !== d));

  const saveAttWindows = async () => {
    try {
      await api.put("/scheduler/attendance-settings", { windows: attWindows, enabled: true });
      toast.success("Attendance reminder windows updated");
      setEditingAttWindows(false);
      const sch = await api.get("/scheduler/status");
      setScheduler(sch.data);
    } catch { toast.error("Failed to save attendance settings"); }
  };

  const runAttendanceCheck = async () => {
    setRunningAtt(true);
    try {
      const { data } = await api.post("/scheduler/run-attendance-now");
      toast.success(`Attendance check: ${data.result?.sent || 0} sent`);
      const sch = await api.get("/scheduler/status");
      setScheduler(sch.data);
    } catch { toast.error("Failed to run attendance check"); }
    finally { setRunningAtt(false); }
  };

  if (loading) return <div className="animate-pulse text-muted-foreground" data-testid="dashboard-loading">Loading dashboard...</div>;
  if (!stats) return <div className="text-muted-foreground">Failed to load dashboard data.</div>;

  const kpis = [
    { label: "Active Students", value: stats.active_students, icon: Users, color: "text-sky-600" },
    { label: "Active Certificates", value: stats.active_certificates, icon: Award, color: "text-emerald-600" },
    { label: "Expiring Soon", value: stats.expiring_soon, icon: AlertTriangle, color: "text-amber-600" },
    { label: "Expired", value: stats.expired || 0, icon: AlertTriangle, color: "text-red-600", sub: stats.expired ? "Action required" : "All current" },
    { label: "Active Courses", value: stats.active_courses_count || 0, icon: CalendarDays, color: "text-violet-600", sub: `${stats.full_courses_count || 0} at full capacity` },
  ];

  const pieData = [
    { name: "Active", value: stats.certificate_by_status?.active || 0 },
    { name: "Expiring", value: stats.certificate_by_status?.expiring_soon || 0 },
    { name: "Expired", value: stats.certificate_by_status?.expired || 0 },
  ].filter(d => d.value > 0);

  const statusBadge = (s) => {
    const map = {
      active: "bg-emerald-100 text-emerald-700 border-emerald-200",
      expiring_soon: "bg-amber-100 text-amber-700 border-amber-200",
      expired: "bg-red-100 text-red-700 border-red-200",
    };
    return map[s] || "bg-slate-100 text-slate-600";
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {kpis.map((kpi, i) => (
          <div key={kpi.label} className={`metric-card border border-border rounded-md p-4 bg-white animate-fade-in stagger-${i + 1}`} data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{kpi.label}</span>
              <kpi.icon size={18} className={kpi.color} />
            </div>
            <p className="font-heading font-bold text-3xl tracking-tight text-foreground">{kpi.value}</p>
            {kpi.sub && <p className="text-[11px] text-muted-foreground mt-1">{kpi.sub}</p>}
          </div>
        ))}
      </div>

      {/* Three-window activity tiles (This FY · Past 5 FYs · All time) —
          same style Jamie asked for on the Analytics page. */}
      <ThreeWindowRow
        title="Students Trained"
        fyLabel={stats.fy_label}
        past5Label={stats.past5_label}
        fyValue={stats.fy_students_trained}
        past5Value={stats.past5_students_trained}
        allValue={stats.all_time_students_trained}
        fyHint="Unique learners on courses this FY"
        past5Hint="Unique learners across the last 5 FYs"
        allHint="Distinct learners on any course ever"
        testid="dash-students-trained"
      />
      <ThreeWindowRow
        title="Certificates Issued"
        fyLabel={stats.fy_label}
        past5Label={stats.past5_label}
        fyValue={stats.fy_certs_issued}
        past5Value={stats.past5_certs_issued}
        allValue={stats.total_certificates}
        fyHint={`Issued ${stats.fy_from || ""} → ${stats.fy_to || ""}`}
        past5Hint="Issued across the last 5 FYs"
        allHint="Every certificate ever issued"
        testid="dash-certs-issued"
      />
      <ThreeWindowRow
        title="Courses Delivered"
        fyLabel={stats.fy_label}
        past5Label={stats.past5_label}
        fyValue={stats.fy_courses_delivered}
        past5Value={stats.past5_courses_delivered}
        allValue={stats.total_courses}
        fyHint="Courses started this FY"
        past5Hint="Courses started in the last 5 FYs"
        allHint="Every course ever scheduled"
        testid="dash-courses-delivered"
      />

      {/* Today's View */}
      {todayData && (
        <div className="border border-border rounded-md bg-white" data-testid="today-section">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-sky-600" />
              <h3 className="font-heading font-semibold text-sm text-foreground">Today — {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</h3>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span><strong className="text-foreground">{todayData.courses_running}</strong> course{todayData.courses_running !== 1 ? "s" : ""} running</span>
              <span><strong className="text-foreground">{todayData.total_attendees}</strong> attendee{todayData.total_attendees !== 1 ? "s" : ""}</span>
            </div>
          </div>
          {todayData.courses?.length > 0 ? (
            <div className="divide-y divide-border">
              {todayData.courses.map(c => (
                <div key={c.id} className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium">{c.title}</p>
                      <p className="text-xs text-muted-foreground">{c.location} | {c.instructor} | {c.start_date} to {c.end_date}</p>
                    </div>
                    <span className={`text-xs font-bold ${c.enrolled >= c.max_capacity ? "text-red-600" : "text-emerald-600"}`}>{c.enrolled}/{c.max_capacity}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {c.attendees?.map((a, i) => (
                      <span key={i} className="text-[10px] bg-slate-100 border border-border rounded-full px-2 py-0.5">{a.name}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-sm text-muted-foreground">No courses running today</div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Certificate Status Chart */}
        <div className="border border-border rounded-md p-4 bg-white" data-testid="cert-status-chart">
          <h3 className="font-heading font-semibold text-sm text-foreground mb-4">Certificate Status</h3>
          {pieData.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width={120} height={120}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={30} outerRadius={55} dataKey="value" strokeWidth={1}>
                    {pieData.map((d, idx) => <Cell key={d.name} fill={COLORS[idx % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {pieData.map((d, i) => (
                  <div key={d.name} className="flex items-center gap-2 text-xs">
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS[i] }} />
                    <span className="text-muted-foreground">{d.name}</span>
                    <span className="font-medium text-foreground">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No certificate data yet</p>
          )}
        </div>

        {/* Quick Stats */}
        <div className="border border-border rounded-md p-4 bg-white" data-testid="quick-stats">
          <h3 className="font-heading font-semibold text-sm text-foreground mb-4">Overview</h3>
          <div className="space-y-3">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total Members</span><span className="font-medium">{stats.total_members}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Qualifications</span><span className="font-medium">{stats.total_qualifications}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total Certificates</span><span className="font-medium">{stats.total_certificates}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total Courses</span><span className="font-medium">{stats.total_courses}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Expired Certs</span><span className="font-medium text-red-600">{stats.expired}</span></div>
          </div>
        </div>

        {/* Members by Role */}
        <div className="border border-border rounded-md p-4 bg-white" data-testid="members-by-role">
          <h3 className="font-heading font-semibold text-sm text-foreground mb-4">Members by Role</h3>
          <div className="space-y-3">
            {Object.entries(stats.members_by_role || {}).map(([role, count]) => (
              <div key={role} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-[10px] capitalize ${
                    role === "admin" ? "bg-sky-100 text-sky-700 border-sky-200" :
                    role === "staff" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                    "bg-amber-100 text-amber-700 border-amber-200"
                  }`}>{role}</Badge>
                </div>
                <span className="text-sm font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Active Courses */}
      <div className="border border-border rounded-md bg-white" data-testid="active-courses-section">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarDays size={16} className="text-violet-600" />
            <h3 className="font-heading font-semibold text-sm text-foreground">Active Courses</h3>
            <Badge variant="outline" className="text-[10px]">{stats.active_courses_count || 0} courses</Badge>
            {(stats.full_courses_count || 0) > 0 && (
              <Badge variant="outline" className="text-[10px] bg-red-100 text-red-700 border-red-200">{stats.full_courses_count} full</Badge>
            )}
          </div>
        </div>
        {(stats.active_courses || []).length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left">
                <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Course</th>
                <th className="px-4 py-2 font-medium text-muted-foreground text-xs hidden md:table-cell">Instructor</th>
                <th className="px-4 py-2 font-medium text-muted-foreground text-xs hidden lg:table-cell">Dates</th>
                <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Capacity</th>
                <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Status</th>
              </tr></thead>
              <tbody>
                {stats.active_courses.map((course, i) => (
                  <tr key={course.id} className="border-b border-border table-row-animate" style={{ animationDelay: `${i * 40}ms` }}>
                    <td className="px-4 py-2">
                      <p className="font-medium">{course.title}</p>
                      <p className="text-xs text-muted-foreground">{course.qualification_category} | {course.location}</p>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground hidden md:table-cell">{course.instructor}</td>
                    <td className="px-4 py-2 text-muted-foreground hidden lg:table-cell text-xs">{course.start_date} to {course.end_date}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <span className={`font-bold ${course.is_full ? "text-red-600" : course.enrolled >= course.max_capacity * 0.8 ? "text-amber-600" : "text-emerald-600"}`}>
                          {course.enrolled}/{course.max_capacity}
                        </span>
                        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${course.is_full ? "bg-red-500" : course.enrolled >= course.max_capacity * 0.8 ? "bg-amber-500" : "bg-emerald-500"}`}
                            style={{ width: `${Math.min((course.enrolled / course.max_capacity) * 100, 100)}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      {course.is_full ? (
                        <Badge variant="outline" className="text-[10px] bg-red-100 text-red-700 border-red-200">FULL</Badge>
                      ) : (
                        <Badge variant="outline" className={`text-[10px] capitalize ${course.status === "in_progress" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-sky-100 text-sky-700 border-sky-200"}`}>
                          {course.status?.replace("_", " ")}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-sm text-muted-foreground">No active courses</div>
        )}
      </div>

      {/* Expiring Certificates */}
      <div className="border border-border rounded-md bg-white" data-testid="expiring-certs-section">
        <div className="p-4 border-b border-border flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-600" />
          <h3 className="font-heading font-semibold text-sm text-foreground">Certificates Requiring Attention</h3>
        </div>
        {stats.expiring_certificates?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left">
                <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Member</th>
                <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Qualification</th>
                <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Expiry Date</th>
                <th className="px-4 py-2 font-medium text-muted-foreground text-xs">Status</th>
              </tr></thead>
              <tbody>
                {stats.expiring_certificates.map((cert, i) => (
                  <tr key={cert.id} className={`border-b border-border table-row-animate`} style={{ animationDelay: `${i * 50}ms` }}>
                    <td className="px-4 py-2">{cert.member_name}</td>
                    <td className="px-4 py-2">{cert.qualification_name}</td>
                    <td className="px-4 py-2">{cert.expiry_date}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className={statusBadge(cert.computed_status)}>
                        {cert.computed_status === "expiring_soon" ? "Expiring" : "Expired"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
            <CheckCircle size={16} className="text-emerald-500" />
            No certificates requiring immediate attention
          </div>
        )}
      </div>

      {/* Automated Reminders */}
      <div className="border border-border rounded-md bg-white" data-testid="scheduler-section">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-sky-600" />
            <h3 className="font-heading font-semibold text-sm text-foreground">Automated Expiry Reminders</h3>
            <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => { setEditingWindows(!editingWindows); setWindows(scheduler?.reminder_windows || []); }} data-testid="edit-windows-btn">
                  <Settings size={12} /> Configure
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={runExpiryCheck} disabled={runningCheck} data-testid="run-expiry-check-btn">
                  <Play size={12} /> {runningCheck ? "Running..." : "Run Now"}
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="p-4 space-y-4">
          {/* Settings editor */}
          {editingWindows && (
            <div className="border border-sky-200 rounded-md p-4 bg-sky-50/50" data-testid="windows-editor">
              <h4 className="text-xs font-medium text-foreground mb-3">Configure Reminder Windows</h4>
              <p className="text-xs text-muted-foreground mb-3">Set how many days before a certificate expires an automatic reminder email will be sent. Add as many windows as you need.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {windows.map(w => (
                  <span key={w} className="flex items-center gap-1 text-xs bg-white border border-border rounded-full pl-2.5 pr-1 py-1 font-medium">
                    {w} days
                    <button onClick={() => removeWindow(w)} className="p-0.5 rounded-full hover:bg-red-100 text-red-400 hover:text-red-600" data-testid={`remove-window-${w}`}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
                {windows.length === 0 && <span className="text-xs text-muted-foreground">No windows set</span>}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number" min="1" max="365" placeholder="Days (e.g. 14)"
                  value={newWindow} onChange={(e) => setNewWindow(e.target.value)}
                  className="w-36 h-8 text-xs"
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addWindow())}
                  data-testid="new-window-input"
                />
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={addWindow} data-testid="add-window-btn">
                  <Plus size={12} className="mr-1" /> Add
                </Button>
                <div className="flex-1" />
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditingWindows(false)}>Cancel</Button>
                <Button size="sm" className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white" onClick={saveWindows} data-testid="save-windows-btn">Save Changes</Button>
              </div>
            </div>
          )}

          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Schedule</p>
              <p className="text-sm font-medium">Daily</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Reminder Windows</p>
              <div className="flex items-center justify-center gap-1 flex-wrap mt-0.5">
                {(scheduler?.reminder_windows || []).map(w => (
                  <Badge key={w} variant="outline" className="text-[10px] px-1.5">{w}d</Badge>
                ))}
              </div>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Auto Emails Today</p>
              <p className="text-sm font-medium">{scheduler?.auto_emails_today || 0}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Total Auto Sent</p>
              <p className="text-sm font-medium">{scheduler?.total_auto_emails || 0}</p>
            </div>
          </div>
          {scheduler?.recent_runs?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Recent Runs</p>
              <div className="space-y-1">
                {scheduler.recent_runs.slice(0, 3).map((run, i) => (
                  <div key={run.id || i} className="flex items-center justify-between text-xs p-2 rounded border border-border bg-slate-50/50">
                    <div className="flex items-center gap-2">
                      <Clock size={12} className="text-muted-foreground" />
                      <span className="text-muted-foreground">{new Date(run.ran_at).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-emerald-600 font-medium">{run.sent} sent</span>
                      {run.failed > 0 && <span className="text-red-600">{run.failed} failed</span>}
                      <span className="text-muted-foreground">{run.skipped} skipped</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Attendance Reminders */}
      <div className="border border-border rounded-md bg-white" data-testid="attendance-section">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-amber-600" />
            <h3 className="font-heading font-semibold text-sm text-foreground">Course Attendance Reminders</h3>
            <Badge variant="outline" className={`text-[10px] ${scheduler?.attendance_enabled ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
              {scheduler?.attendance_enabled ? "Active" : "Disabled"}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => { setEditingAttWindows(!editingAttWindows); setAttWindows(scheduler?.attendance_windows || []); }} data-testid="edit-att-windows-btn">
                  <Settings size={12} /> Configure
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={runAttendanceCheck} disabled={runningAtt} data-testid="run-attendance-check-btn">
                  <Play size={12} /> {runningAtt ? "Running..." : "Run Now"}
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="p-4 space-y-4">
          {editingAttWindows && (
            <div className="border border-amber-200 rounded-md p-4 bg-amber-50/50" data-testid="att-windows-editor">
              <h4 className="text-xs font-medium text-foreground mb-3">Configure Attendance Reminder Windows</h4>
              <p className="text-xs text-muted-foreground mb-3">Set how many days before a course start date enrolled learners get an automatic reminder email. Recommended: 14, 7, 1 days.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {attWindows.map(w => (
                  <span key={w} className="flex items-center gap-1 text-xs bg-white border border-border rounded-full pl-2.5 pr-1 py-1 font-medium">
                    {w} days
                    <button onClick={() => removeAttWindow(w)} className="p-0.5 rounded-full hover:bg-red-100 text-red-400 hover:text-red-600" data-testid={`remove-att-window-${w}`}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
                {attWindows.length === 0 && <span className="text-xs text-muted-foreground">No windows set</span>}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number" min="1" max="60" placeholder="Days (e.g. 7)"
                  value={newAttWindow} onChange={(e) => setNewAttWindow(e.target.value)}
                  className="w-36 h-8 text-xs"
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAttWindow())}
                  data-testid="new-att-window-input"
                />
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={addAttWindow} data-testid="add-att-window-btn">
                  <Plus size={12} className="mr-1" /> Add
                </Button>
                <div className="flex-1" />
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditingAttWindows(false)}>Cancel</Button>
                <Button size="sm" className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white" onClick={saveAttWindows} data-testid="save-att-windows-btn">Save Changes</Button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Schedule</p>
              <p className="text-sm font-medium">Daily</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Reminder Windows</p>
              <div className="flex items-center justify-center gap-1 flex-wrap mt-0.5" data-testid="att-windows-display">
                {(scheduler?.attendance_windows || []).map(w => (
                  <Badge key={w} variant="outline" className="text-[10px] px-1.5">{w}d</Badge>
                ))}
              </div>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Onboarding Emails</p>
              <p className="text-sm font-medium">Sent on enrolment</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
